import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  getExpandedRowModel,
  getGroupedRowModel,
  getCoreRowModel,
  getFacetedRowModel,
  getFacetedUniqueValues,
  getFilteredRowModel,
  getSortedRowModel,
  useReactTable,
  getPaginationRowModel,
  type ColumnDef,
  type ColumnFiltersState,
  type GroupingState,
  type PaginationState,
  type Row,
  type SortingState,
} from "@tanstack/react-table";
import Thead from "./Thead/Thead";
import Tbody from "./Tbody/Tbody";
import "./Table.scss";
import TableLoader from "./TableLoader/TableLoader";
import Loader from "../Loader/Loader";
import { useVirtual } from "react-virtual";
import TablePagination from "./TablePagination/TablePagination";
import TableActions from "./TableActions/TableActions";
import { arrayMove } from "@dnd-kit/sortable";
import {
  closestCenter,
  DndContext,
  KeyboardSensor,
  MouseSensor,
  TouchSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from "@dnd-kit/core";
import { restrictToHorizontalAxis } from "@dnd-kit/modifiers";
import { useDebounce } from "react-use";
import classNames from "classnames";

export const sortModeEnum = {
  NONE: "NONE",
  EDIT: "EDIT",
  READ_ONLY: "READ_ONLY",
} as const;

export type SortMode = (typeof sortModeEnum)[keyof typeof sortModeEnum];

export type TableCellType = "text" | "component";

// Custom columnDef field used across the Table design-system components but not
// present on the base @tanstack/react-table ColumnDef. Mirrors SortPopover's
// ExtendedColumnDef precedent; narrowed locally at access sites.
type AccessorColumnDef = { accessorKey?: string };

export interface TableCellProps {
  children?: React.ReactNode;
  type?: TableCellType;
  multiLine?: boolean;
  className?: string;
  onClick?: (e: React.MouseEvent<HTMLDivElement>) => void;
  "data-testid"?: string;
}

// TODO: type more props as the Table API is fleshed out; the index signature
// preserves the permissive shape declared in the original Table.d.ts so that
// existing consumers keep compiling.
export interface TableProps<TData = any> {
  columns?: ColumnDef<TData, any>[];
  // null is tolerated at runtime (internally coalesced via `data || []`).
  data?: TData[] | null;
  forceGrouping?: boolean;
  defaultGrouping?: string[];
  paginationComponent?: React.ReactNode;
  manualSorting?: boolean;
  sortOnHeaderClick?: boolean;
  onSortingChange?: (sorting: SortingState) => void;
  manualFiltering?: boolean;
  onColumnFiltersChange?: (columnFilters: ColumnFiltersState) => void;
  defaultPageIndex?: number;
  /**
   * Rows that can open a sub-component. A flat row only toggles through a cell
   * the consumer renders with `row.getToggleExpandedHandler()`; row click only
   * toggles rows with subRows.
   */
  getRowCanExpand?: (row: Row<TData>) => boolean;
  /**
   * Full-width row rendered under an expanded row. Static rows only (ignored
   * with `infiniteScroll`); rows with subRows (grouping) never render it.
   * Flat rows open only via a consumer-rendered toggle cell (see `getRowCanExpand`).
   * Called as a plain function during render: return an element such as
   * `<Panel row={row} />` and keep hooks inside that component. Wrap the content
   * in an element with `className="td-detail-inset"` for the inset panel look.
   * A panel that throws renders nothing instead of unmounting the table.
   */
  renderSubComponent?: (props: { row: Row<TData> }) => React.ReactNode;
  [key: string]: any;
}

const Table: React.FC<TableProps> = ({
  error = undefined,
  dataId = undefined,
  columns = [],
  data = undefined,
  loaderRowsAmount = 40,
  noDataIcon = undefined,
  noDataText = "No Data",
  noDataLoader = null,
  noSearchResultsTitle = "No results",
  noSearchResultsSubtitle = "Try another keyword",
  onRowClick = undefined,
  initialSortBy = [],
  initialFilters = [],
  size = "small",
  loading = undefined,
  loadingMore = undefined,
  noBorder = false,
  rowBorders = true,
  rowHeight = 56,
  "data-testid": dataTestId = "",
  infiniteScroll = false,
  hasNextPage = false,
  fetchNextPage = undefined,
  selectedRowId = undefined,
  enableRowSelection = false,
  withPagination = false,
  stickyPagination = true,
  manualPagination = true,
  totalRowsCount = null,
  loadMoreByPage = undefined,
  onLoadMoreByPage = undefined,
  shouldResetPagination = undefined,
  columnsConfig = {},
  className = undefined,
  wrapperClassName = undefined,
  getSelectAllAlertComponent = null,
  getActionComponent = undefined,
  enableTableViewEdit = false,
  sortMode = sortModeEnum.NONE,
  sortHelp = undefined,
  onExpandTable = undefined,
  onTableViewChange = null,
  resetColumns = null,
  defaultSortBy = null,
  pageSizeEnabled = true,
  autoResetExpanded = false,
  onSortChange = null,
  manualSorting = undefined,
  sortOnHeaderClick = false,
  onSortingChange = undefined,
  manualFiltering = false,
  onColumnFiltersChange = undefined,
  defaultPageIndex = 0,
  defaultPageSize = 50,
  pageSizeOptionsList = null,
  noBackground = false,
  forceGrouping = false,
  defaultGrouping = [],
  paginationComponent,
  extraToolbarContent,
  getRowCanExpand = undefined,
  renderSubComponent = undefined,
}) => {
  const { visibleColumns, pinnedColumns, columnSizes, columnMinSizes } =
    columnsConfig;
  const tableContainerRef = useRef<HTMLDivElement>(null);
  const [sorting, setSorting] = useState<SortingState>(initialSortBy);
  const [columnFilters, setColumnFilters] =
    useState<ColumnFiltersState>(initialFilters);
  const [prevScrollPosition, setPrevScrollPosition] = useState(0);
  const [rowSelection, setRowSelection] = useState<Record<string, boolean>>({});
  const [columnVisibility, setColumnVisibility] = useState<
    Record<string, boolean>
  >({});
  const [columnOrder, setColumnOrder] = useState<string[]>(visibleColumns);
  const [columnPinning, setColumnPinning] = useState<Record<string, any>>({});
  const [grouping, setGrouping] = useState<GroupingState>(defaultGrouping);
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: defaultPageIndex,
    pageSize: defaultPageSize,
  });

  const sizedColumns = useMemo(() => {
    if (!!columnSizes) {
      return [...columns].map((c) => ({
        ...c,
        size: columnSizes[(c as AccessorColumnDef).accessorKey!],
        minSize: c?.minSize || columnMinSizes,
      }));
    }
    return [...columns].map((c) => ({
      ...c,
      minSize: c?.minSize || columnMinSizes,
    }));
  }, [columnSizes, columns, visibleColumns]);

  const table = useReactTable({
    data: data || [],
    autoResetExpanded,
    columns: sizedColumns,
    defaultColumn: {
      minSize: columnMinSizes,
    },
    state: {
      sorting,
      columnFilters,
      rowSelection,
      columnVisibility,
      columnOrder,
      columnPinning,
      grouping,
      pagination,
    },
    onSortingChange: (value) => {
      const nextSorting = typeof value === "function" ? value(sorting) : value;
      setSorting(nextSorting);
      // needs manual reset due to autoResetPageIndex: false
      setPagination((prev) => ({ ...prev, pageIndex: 0 }));
      onSortingChange?.(nextSorting);
    },
    onColumnFiltersChange: (value) => {
      const nextColumnFilters =
        typeof value === "function" ? value(columnFilters) : value;
      setColumnFilters(nextColumnFilters);
      // needs manual reset due to autoResetPageIndex: false
      setPagination((prev) => ({ ...prev, pageIndex: 0 }));
      onColumnFiltersChange?.(nextColumnFilters);
    },
    onColumnVisibilityChange: setColumnVisibility,
    onColumnOrderChange: setColumnOrder,
    onColumnPinningChange: setColumnPinning,
    onGroupingChange: setGrouping,
    enableSortingRemoval: false,
    enableRowSelection: enableRowSelection,
    sortDescFirst: false,
    onRowSelectionChange: setRowSelection,
    getFilteredRowModel: getFilteredRowModel(),
    getExpandedRowModel: getExpandedRowModel(),
    getRowCanExpand,
    getGroupedRowModel: getGroupedRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getCoreRowModel: getCoreRowModel(),
    getFacetedRowModel: getFacetedRowModel(),
    getFacetedUniqueValues: getFacetedUniqueValues(),
    ...(!manualPagination
      ? {
          getPaginationRowModel: getPaginationRowModel(),
          onPaginationChange: setPagination,
          // Set to false to handle cases where certain column data (e.g., findings count and ticket status on the monitoring page) are populated separately.
          // This prevents the pagination from resetting to the first page when new column data has returned.
          autoResetPageIndex: false,
        }
      : {}),
    manualPagination,
    manualFiltering,
    manualSorting: manualSorting ?? sortMode !== sortModeEnum.NONE,
    columnResizeMode: (enableTableViewEdit ? "onChange" : "") as "onChange",
    getRowId: (originalRow) => originalRow?.id,
  });

  const columnsWidth = table.getHeaderGroups().map((headerGroup) =>
    headerGroup.headers.map((header) => {
      return header.getSize();
    })
  );

  const fetchMoreOnBottomReached = useCallback(
    (containerRefElement: HTMLDivElement | null) => {
      if (containerRefElement) {
        const { scrollHeight, scrollTop, clientHeight } = containerRefElement;
        //once the user has scrolled within 300px of the bottom of the table, fetch more data if there is any

        const scrollingDown = scrollTop > prevScrollPosition;
        if (
          scrollHeight - scrollTop - clientHeight <= 500 &&
          !loading &&
          !loadingMore &&
          hasNextPage &&
          scrollingDown
        ) {
          fetchNextPage();
        }
        setPrevScrollPosition(scrollTop);
      }
    },
    [prevScrollPosition, loading, loadingMore, hasNextPage, fetchNextPage]
  );

  const { rows } = table.getRowModel();

  const rowVirtualizer = useVirtual({
    parentRef: tableContainerRef,
    size: rows.length,
    overscan: 10,
    paddingEnd: 5,
  });

  const { virtualItems: virtualRows, totalSize } = rowVirtualizer;

  // reset the scroll to 0 when table id is changed (new set of data)
  useEffect(() => {
    rowVirtualizer.scrollToOffset(0);
    // reset checked rows when data changes
    setRowSelection({});
    // reset column filters and sorts when data changes
    setColumnFilters(initialFilters);
    setSorting(initialSortBy);
  }, [dataId]);

  const paddingTop = virtualRows.length > 0 ? virtualRows?.[0]?.start || 0 : 0;
  const paddingBottom =
    virtualRows.length > 0
      ? totalSize - (virtualRows?.[virtualRows.length - 1]?.end || 0)
      : 0;

  const getTableBodyComponent = () => {
    if (loading || !data) {
      return (
        <TableLoader
          rowHeight={rowHeight}
          rows={loaderRowsAmount}
          colsWidth={columnsWidth?.[0]}
          data-testid={dataTestId}
          hasSelection={enableRowSelection}
        />
      );
    }
    if (error) {
      return (
        <tbody className={"tbody"}>
          <tr>
            <td colSpan={100} className={"table-center"}>
              Error Loading Data
            </td>
          </tr>
        </tbody>
      );
    }

    if (data?.length === 0 && !loading) {
      return (
        <tbody className={"tbody"}>
          <tr>
            {!!noDataLoader ? (
              noDataLoader
            ) : (
              <Loader
                icon={noDataIcon}
                center
                largeTitle
                smallIcon
                title={noDataText}
                wrapperHeight
              />
            )}
          </tr>
        </tbody>
      );
    }

    return (
      <Tbody
        infiniteScroll={infiniteScroll}
        size={size}
        noSearchResultsTitle={noSearchResultsTitle}
        noSearchResultsSubtitle={noSearchResultsSubtitle}
        data-testid={dataTestId}
        rows={rows}
        virtualRows={virtualRows}
        onRowClick={onRowClick}
        rowHeight={rowHeight}
        loadingMore={loadingMore}
        styleProps={{ paddingBottom, paddingTop }}
        selectedRowId={selectedRowId}
        enableTableViewEdit={enableTableViewEdit}
        lastPinnedColumn={lastPinnedColumn}
        columns={table.getAllLeafColumns()}
        renderSubComponent={renderSubComponent}
      />
    );
  };

  const loadMoreByPaginationState = useCallback(
    (pageSize: number, pageIndex: number) => {
      setPagination((prev) => ({ ...prev, pageIndex: pageIndex - 1 }));
      onLoadMoreByPage?.();
    },
    []
  );

  const getTablePaginationComponent = () => {
    if (paginationComponent) {
      return (
        <div className="table-pagination-wrapper">{paginationComponent}</div>
      );
    }

    const totalCount = manualPagination ? totalRowsCount : table.getRowCount();
    const loadMore = manualPagination
      ? loadMoreByPage
      : loadMoreByPaginationState;

    return (
      <div
        className={`table-pagination-wrapper ${!totalCount ? "hidden" : ""}`}
      >
        <TablePagination
          totalCount={totalCount}
          loadMoreByPage={loadMore}
          loading={loading}
          shouldResetPagination={shouldResetPagination}
          resetSelection={() =>
            enableRowSelection ? setRowSelection({}) : null
          }
          pageIndex={pagination?.pageIndex + 1}
          setPageIndex={(index: number) =>
            setPagination((prev) => ({ ...prev, pageIndex: index - 1 }))
          }
          pageSize={pagination?.pageSize}
          setPageSize={(size: number) => {
            setPagination((prev) => ({ ...prev, pageSize: size }));
          }}
          pageSizeEnabled={pageSizeEnabled}
          pageSizeOptionsList={pageSizeOptionsList}
        />
      </div>
    );
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (active?.id !== "selector" && over?.id !== "selector") {
      if (active && over && active.id !== over.id) {
        setColumnOrder((columnOrder) => {
          const oldIndex = columnOrder.indexOf(active.id as string);
          const newIndex = columnOrder.indexOf(over.id as string);
          return arrayMove(columnOrder, oldIndex, newIndex);
        });
      }
    }
  };

  const sensors = useSensors(
    useSensor(MouseSensor, {}),
    useSensor(TouchSensor, {}),
    useSensor(KeyboardSensor, {})
  );

  // Setting saved column configuration
  useEffect(() => {
    const visibleColumnsMap = columns?.reduce(
      (acc: Record<string, boolean>, c) => {
        const columnKey = ((c as AccessorColumnDef)?.accessorKey || c?.id)!;
        if (!visibleColumns?.length || !!visibleColumns.includes(columnKey)) {
          acc[columnKey] = true;
        } else {
          acc[columnKey] = false;
        }
        return acc;
      },
      {}
    );

    table.setColumnVisibility(visibleColumnsMap);
    table.setColumnOrder(visibleColumns);
  }, [visibleColumns, pinnedColumns, columns?.length]);

  useEffect(() => {
    table.setColumnPinning({
      left: !!pinnedColumns?.length
        ? pinnedColumns
        : [enableTableViewEdit && "selector"],
    });
  }, [pinnedColumns]);

  const lastPinnedColumn = useMemo(
    () =>
      Math.max(
        ...table.getVisibleFlatColumns()?.map((c) => c.getPinnedIndex())
      ),
    [columnPinning]
  );

  const columnSizesMap = useMemo(
    () =>
      table
        .getVisibleFlatColumns()
        ?.reduce((acc: Record<string, number>, c) => {
          acc[c.id] = c.getSize();
          return acc;
        }, {}),
    [columnsWidth]
  );

  useDebounce(
    () => {
      if (enableTableViewEdit && !!onTableViewChange) {
        onTableViewChange({
          visibleColumns: Object.entries(columnVisibility)
            ?.map((c) => (!!c[1] ? c[0] : null))
            ?.filter((c) => !!c),
          columnOrder,
          pinnedColumns: columnPinning?.left,
          columnsWidth: columnSizesMap,
        });
      }
    },
    500,
    [columnPinning, columnVisibility, columnOrder, columnSizesMap]
  );

  const groupingEnabledColumns = useRef(
    table?.getHeaderGroups()?.reduce((acc: any[], curr) => {
      return [
        ...acc,
        ...curr.headers.filter(
          (header) =>
            (header?.column?.columnDef?.meta as { groupingLabel?: string })
              ?.groupingLabel
        ),
      ];
    }, [])
  );

  const classNameWrapper = classNames(
    "table-container",
    {
      [size === "small" ? "is-small" : "is-large"]: true,
      "has-no-border": noBorder,
      "has-no-row-borders": !rowBorders,
      "is-loading": loading,
      "no-data": (!data?.length && !loading) || error,
      "has-no-click": !onRowClick,
      "has-pagination": withPagination || !!paginationComponent,
      "has-grouping": groupingEnabledColumns?.current?.length,
      "pagination-not-sticky": !stickyPagination,
    },
    wrapperClassName
  );

  return (
    <div
      className={`table-control-wrap ${noBackground ? "no-background" : ""}`}
    >
      <TableActions
        setGrouping={setGrouping}
        grouping={grouping}
        table={table}
        getActionComponent={getActionComponent}
        enableColumnChoosing={enableTableViewEdit}
        sortMode={sortMode}
        sortHelp={sortHelp}
        onExpandTable={onExpandTable}
        forceGrouping={forceGrouping}
        extraToolbarContent={extraToolbarContent}
        resetColumns={() => {
          resetColumns?.();
        }}
        defaultSortBy={defaultSortBy}
        onSortChange={(sortBy: { id?: string }[]) => {
          onSortChange?.(sortBy);
          const columnsToAdd = sortBy?.map((sort) => {
            const tableColumn = table.getColumn(sort?.id!);
            if (!!tableColumn && !tableColumn?.getIsVisible()) {
              // if the column is hidden, we should show it
              tableColumn.getToggleVisibilityHandler()({
                target: { checked: true },
              });
              return (tableColumn?.columnDef as AccessorColumnDef)?.accessorKey;
            }
          });
          // And append the new columns to the end of the column order
          setColumnOrder((prev) => [...prev, ...(columnsToAdd as string[])]);
        }}
        tableLoading={loading}
      />
      <div
        className={classNameWrapper}
        onScroll={(e) => {
          infiniteScroll &&
            fetchMoreOnBottomReached(e.target as HTMLDivElement);
        }}
        ref={tableContainerRef}
      >
        <DndContext
          collisionDetection={closestCenter}
          modifiers={[restrictToHorizontalAxis]}
          onDragEnd={handleDragEnd}
          sensors={sensors}
        >
          <table
            data-testid={`${dataTestId}-table`}
            className={`table-wrapper ${className ? className : ""} ${
              !!Object?.keys(rowSelection).length ? "has-selected" : ""
            } ${enableRowSelection ? "has-row-selection" : ""}`}
            {...{
              style: {
                width: enableTableViewEdit
                  ? `${table?.getTotalSize()}px`
                  : "calc(100% - 2px)",
              },
            }}
          >
            <Thead
              data-table_child={"header"}
              data-testid={`${dataTestId}-table-header`}
              table={table}
              size={size}
              rowHeight={rowHeight}
              enableTableViewEdit={enableTableViewEdit}
              lastPinnedColumn={lastPinnedColumn}
              getSelectAllAlertComponent={getSelectAllAlertComponent}
              hasComplexSorting={sortMode !== sortModeEnum.NONE}
              sortOnHeaderClick={sortOnHeaderClick}
            />
            {getTableBodyComponent()}
          </table>
        </DndContext>
      </div>
      {(withPagination || !!paginationComponent) &&
        getTablePaginationComponent()}
    </div>
  );
};

// TODO: temporary typed alias preserved from the original Table.d.ts so any
// type-only consumers importing `TypedTable` keep resolving.
export const TypedTable = Table as React.FC<TableProps>;

export default Table;
